import { formatCodeName } from '../common/utils';
import { BaseDataObjectGroupDto, DataObjectSubgroupDto } from './schema/types';

export interface DataObjectGroupFlatOption {
  label: string;
  value: string;
}

export interface DataObjectGroupTreeNode {
  value: string;
  title: string;
  selectLabel: string;
  children?: DataObjectGroupTreeNode[];
}

export interface SelectedDataObjectGroupIds {
  selectedGroupIds?: number[];
  selectedSubgroupIds?: number[];
}

export const deduplicateDataObjectGroups = (
  groups: BaseDataObjectGroupDto[],
): BaseDataObjectGroupDto[] => {
  const groupsById = new Map<number, BaseDataObjectGroupDto>();
  groups.forEach((group) => {
    if (!groupsById.has(group.id)) {
      groupsById.set(group.id, group);
    }
  });
  return Array.from(groupsById.values());
};

export const getDataObjectGroupsFromOptionPages = (
  pages: { content: { item: BaseDataObjectGroupDto }[] }[] = [],
  defaultOption?: BaseDataObjectGroupDto,
): BaseDataObjectGroupDto[] =>
  deduplicateDataObjectGroups([
    ...(defaultOption ? [defaultOption] : []),
    ...pages.flatMap((page) => page.content.map((option) => option.item)),
  ]);

export const getDataObjectGroupFlatOptions = (
  groups: BaseDataObjectGroupDto[],
): DataObjectGroupFlatOption[] =>
  groups.flatMap((group) => {
    const label = formatCodeName(group);
    return [
      { label, value: `g-${group.id}` },
      ...group.subgroups.flatMap((subgroup) =>
        getSubgroupFlatOptions(label, subgroup),
      ),
    ];
  });

const getSubgroupFlatOptions = (
  parentLabel: string,
  subgroup: DataObjectSubgroupDto,
): DataObjectGroupFlatOption[] => {
  const label = `${parentLabel} / ${subgroup.name}`;
  const childOptions = subgroup.children.flatMap((child) =>
    getSubgroupFlatOptions(label, child),
  );

  return [{ label, value: `s-${subgroup.id}` }, ...childOptions];
};

const getSubgroupIdsById = (
  groups: BaseDataObjectGroupDto[],
): Map<number, number[]> => {
  const subgroupIdsById = new Map<number, number[]>();

  const visit = (subgroup: DataObjectSubgroupDto): number[] => {
    const ids = [
      subgroup.id,
      ...subgroup.children.flatMap((child) => visit(child)),
    ];
    subgroupIdsById.set(subgroup.id, ids);
    return ids;
  };

  groups.forEach((group) => group.subgroups.forEach(visit));
  return subgroupIdsById;
};

export const expandDataObjectGroupFlatValues = (
  values: string[] | null | undefined,
  groups: BaseDataObjectGroupDto[],
): string[] | null | undefined => {
  if (!values) {
    return values;
  }

  const subgroupIdsById = getSubgroupIdsById(groups);
  return values.map((value) => {
    if (!value.startsWith('s-')) {
      return value;
    }

    const [subgroupId] = getIdsFromValue(value);
    const subgroupIds = subgroupIdsById.get(subgroupId);
    return subgroupIds ? `s-${subgroupIds.join('-')}` : value;
  });
};

export const getDataObjectGroupTreeData = (
  groups: BaseDataObjectGroupDto[],
): DataObjectGroupTreeNode[] =>
  groups.map((group) => {
    const title = formatCodeName(group);
    return {
      value: group.id.toString(),
      title,
      selectLabel: title,
      children: group.subgroups.length
        ? group.subgroups.map((subgroup) =>
            getSubgroupTreeNode(group.id, title, subgroup),
          )
        : undefined,
    };
  });

const getSubgroupTreeNode = (
  groupId: number,
  parentSelectLabel: string,
  subgroup: DataObjectSubgroupDto,
): DataObjectGroupTreeNode => {
  const selectLabel = `${parentSelectLabel} / ${subgroup.name}`;
  return {
    value: `${groupId}-${subgroup.id}`,
    title: subgroup.name,
    selectLabel,
    children: subgroup.children.length
      ? subgroup.children.map((child) =>
          getSubgroupTreeNode(groupId, selectLabel, child),
        )
      : undefined,
  };
};

const getIdsFromValue = (value: string): number[] =>
  value.split('-').map(Number).filter(Number.isSafeInteger);

const optionalIds = (ids: number[]) => {
  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.length ? uniqueIds : undefined;
};

export const getSelectedIdsFromFlatValues = (
  values?: string[] | null,
): SelectedDataObjectGroupIds => ({
  selectedGroupIds: optionalIds(
    values
      ?.filter((value) => value.startsWith('g-'))
      .flatMap(getIdsFromValue) ?? [],
  ),
  selectedSubgroupIds: optionalIds(
    values
      ?.filter((value) => value.startsWith('s-'))
      .flatMap(getIdsFromValue) ?? [],
  ),
});

export const getSelectedIdsFromTreeValue = (
  value?: string | null,
): SelectedDataObjectGroupIds => {
  const [groupId, subgroupId] = value ? getIdsFromValue(value) : [];
  return {
    selectedGroupIds: optionalIds(groupId === undefined ? [] : [groupId]),
    selectedSubgroupIds: optionalIds(
      subgroupId === undefined ? [] : [subgroupId],
    ),
  };
};
